// BatiAssistAutomatisationPage.jsx
// Page Automatisation & IA dans le contexte Espace Pro Abracadabati

import { motion } from 'framer-motion';
import { 
  Bot, ArrowRight, Zap, Mail, MessageSquare, 
  Calendar, FileText, Bell, Cpu, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const automations = [
  { icon: Mail, title: "Emails automatiques", desc: "Envoie des emails de suivi, de relance ou de confirmation sans lever le petit doigt" },
  { icon: MessageSquare, title: "SMS & WhatsApp", desc: "Rappels de rendez-vous, confirmations de devis envoyés automatiquement" },
  { icon: Calendar, title: "Prise de RDV", desc: "Tes clients réservent eux-mêmes leurs créneaux selon ta disponibilité" },
  { icon: FileText, title: "Génération de docs", desc: "Devis, factures, attestations générés automatiquement" },
  { icon: Bell, title: "Notifications", desc: "Sois alerté des événements importants : nouveau prospect, devis signé..." },
  { icon: Cpu, title: "IA intégrée", desc: "L'IA analyse tes données et te suggère les meilleures actions à prendre" },
];

const benefits = [
  { value: "10h", label: "Gagnées par semaine" },
  { value: "0", label: "Tâche oubliée" },
  { value: "24/7", label: "Disponibilité" },
  { value: "+30%", label: "Productivité" },
];

export default function BatiAssistAutomatisationPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-automatisation-page">
        <UnifiedHeader />
        <EspaceProNav />
      
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/10 text-[#F97316] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <Bot className="w-4 h-4" />
                Automatisation & IA
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Automatise les tâches{' '}
                <span className="text-[#F97316]">répétitives</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10">
                Gagne des heures chaque semaine grâce à l'automatisation intelligente. 
                L'IA travaille pour toi pendant que tu te concentres sur tes chantiers.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                  Découvrir l'automatisation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-[#1E3A5F]">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <p className="text-4xl md:text-5xl font-bold text-[#F97316]">{benefit.value}</p>
                  <p className="text-white/80 mt-2">{benefit.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Automations */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Ce que l'<span className="text-[#F97316]">automatisation</span> fait pour toi
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {automations.map((auto, idx) => {
                const Icon = auto.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 bg-slate-50 rounded-2xl hover:shadow-lg transition-shadow"
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{auto.title}</h3>
                    <p className="text-slate-600">{auto.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* AI Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="w-16 h-16 text-[#F97316] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                L'IA qui comprend ton métier
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Notre intelligence artificielle a été entraînée sur des milliers de données du bâtiment. 
                Elle comprend tes enjeux et te propose des actions pertinentes.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full px-10 py-6 text-lg">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
